import { Input, TextField } from '@shared/ui/kit';
import { FC, ReactNode, useEffect, useState, useCallback } from 'react';
import stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { phraseValidator } from '@pages/event/lib/validator';
import CategoryParticipantsList from '@entities/add-category-modal/category-participants-list';
import EmployeeSwitcher from '@entities/employee-switcher';
import WithExcel from '@features/add-participants-modal/with-excel';
import { SuccessIcon, ErrorIcon, XmarkIcon } from '@shared/assets/icons';
import { useCheckCategoryName, useCreateCategory } from '@pages/event/api/useCreateCategory';
import { User } from '@pages/event/api/useFetchEventParticipants';
import { GuestsData, GuestsDataRaw, useAddParticipantsToCategory } from '@pages/event/api/useAddParticipantsToCategory';
import { ModalWrapper } from '@shared/ui/modal-wrapper';
import { InvitedParticipants } from '@widgets/segments-tab';
import { CategoryColorPicker } from '@entities/category';
import { FreelanceEmployee } from '@features/add-participants-modal/freelance-employee';
import { IFreelanceEmployee } from '@shared/types/types';
import { FreelanceEmployeeStep, PARTICIPANT_TAB_INDEX } from '@features/add-participants-modal/model/types';
import { ErrorData, useCreateUser } from '@features/api/use-create-user';
import { useUpdateUser } from '@features/api/use-update-user';
import { cleanPhoneNumber } from '@shared/libs/clean-number';
import { useToast } from '@shared/hooks/useToast';
import { CreateFreelanceResultModal } from '@entities/freelance-employee';
import { CORRECT_PHONE_LENGTH } from '@shared/constants/errorMessages';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import { AddParticipantsCBToCategoryModal } from '@entities/participants';
import { Category } from '@pages/event/api/useFetchCategory';

type ModalState = 'success' | 'error' | 'warning' | 'reassign' | null;

const STATUSES_CREATE_EMPLOYEE = {
  default: '',
  success: 'success',
  error: 'error',
};

const CATEGORY_MODAL_STEPS = {
  CREATE_FORM: 1,
  ADD_PARTICIPANTS: 2,
  RESULT: 3,
};

const NAME_MAX_LENGTH = 50;
const DESCRIPTION_MAX_LENGTH = 200;

interface ModalAddCategoryProps {
  active: boolean;
  closeHandle: () => void;
  eventId: number | undefined;
  refetch: () => void;
  isCBGroup?: boolean;
}

export const ModalAddCategory: FC<ModalAddCategoryProps> = ({
  active,
  closeHandle,
  eventId,
  refetch,
  isCBGroup = false,
}) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [color, setColor] = useState<string>();
  const [categoryNameErrors, setCategoryNameErrors] = useState<string | false>(false);
  const [step, setStep] = useState<number>(CATEGORY_MODAL_STEPS.CREATE_FORM);
  const [modalState, setModalState] = useState<ModalState>(null);
  const [selectedParticipants, setSelectedParticipants] = useState<User['id'][]>([]);
  const [participantsDelete, setParticipantsDelete] = useState<InvitedParticipants[]>([]);
  const [isReadyCheckRequest, setIsReadyCheckRequest] = useState(false);
  const [totalCount, setTotalCount] = useState<number>();
  const [guestsData, setGuestsData] = useState<GuestsData[]>([]);
  const [checkCategoryNameValues, setCheckCategoryNameValues] = useState<{ title: string; eventId: number }>({
    title: '',
    eventId: 1,
  });
  const { showToast } = useToast();
  const [freelanceEmployees, setFreelanceEmployees] = useState<IFreelanceEmployee[]>([]);
  const [guestSelectedParticipants, setGuestSelectedParticipants] = useState<number[]>([]);
  const [freelanceStep, setFreelanceStep] = useState<FreelanceEmployeeStep>(FreelanceEmployeeStep.STRUCTURE);
  const [editableEmployee, setEditableEmployee] = useState<IFreelanceEmployee>();
  const [errorsCreateEmployee, setErrorsCreateEmployee] = useState<ErrorData[]>([]);
  const [hasInvalidEmail, setHasInvalidEmail] = useState(false);
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
  const [statusCreateEmployee, setStatusCreateEmployee] = useState(STATUSES_CREATE_EMPLOYEE.default);
  const { createUser } = useCreateUser();
  const { updateUser } = useUpdateUser();
  const isAddEmployee =
    activeIndex === PARTICIPANT_TAB_INDEX.FREELANCE_EMPLOYEE && freelanceStep === FreelanceEmployeeStep.FORM;

  const onEmailError = useCallback((hasError: boolean) => {
    setHasInvalidEmail(hasError);
  }, []);

  const onCloseFeedbackModal = () => {
    setStatusCreateEmployee(STATUSES_CREATE_EMPLOYEE.default);
    setFreelanceStep(FreelanceEmployeeStep.STRUCTURE);
    setFreelanceEmployees([]);
    setEditableEmployee(undefined);
  };

  useEffect(() => {
    if (freelanceEmployees?.length) {
      const isValid = freelanceEmployees.every((employee) => {
        return (
          employee.firstName &&
          employee.lastName &&
          employee.firstName.trim() !== '' &&
          employee.lastName.trim() !== '' &&
          employee.phone &&
          employee.email &&
          cleanPhoneNumber(employee.phone).length >= CORRECT_PHONE_LENGTH
        );
      });
      setIsSaveButtonDisabled(!isValid || hasInvalidEmail);
    } else {
      setIsSaveButtonDisabled(false);
    }
  }, [freelanceEmployees, hasInvalidEmail]);

  const [{ data: checkCategoryData }, executeQuery] = useCheckCategoryName({
    ...checkCategoryNameValues,
    pause: !isReadyCheckRequest,
  });
  const [, createCategory] = useCreateCategory();
  const [, addParticipantsToCategory] = useAddParticipantsToCategory();

  useEffect(() => {
    if (!categoryName) {
      setCategoryNameErrors('');
      return;
    }

    if (categoryName.length > NAME_MAX_LENGTH) {
      setCategoryNameErrors('Максимальная длинна текста 50 символов');
      return;
    }

    phraseValidator(categoryName).then(setCategoryNameErrors);
  }, [categoryName]);

  useEffect(() => {
    if (checkCategoryData?.checkCategory?.exist) {
      setCategoryNameErrors('Категория с таким названием уже существует');
      setIsReadyCheckRequest(false);
      return;
    }
    if (isReadyCheckRequest && !checkCategoryData?.checkCategory?.exist) {
      setStep(step + 1);
    }
  }, [checkCategoryData]);

  const validateCategoryName = useCallback(() => {
    if (!categoryName) {
      setCategoryNameErrors('Наименование не может быть пустым');
      return false;
    }
    if (categoryName.length < 4) {
      setCategoryNameErrors('Наименование должно содержать не менее 4 символов.');
      return false;
    }
    if (categoryName.length > 50) {
      setCategoryNameErrors('Достигнут максимальный лимит символов');
      return false;
    }
    return true;
  }, [categoryName]);

  const resetModal = useCallback(() => {
    setStep(CATEGORY_MODAL_STEPS.CREATE_FORM);
    setModalState(null);
    setCategoryName('');
    setCategoryDescription('');
    setCategoryNameErrors(false);
    setIsReadyCheckRequest(false);
    setCheckCategoryNameValues({ title: '', eventId: 1 });
    setSelectedParticipants([]);
    setParticipantsDelete([]);
    setGuestsData([]);
    setTotalCount(undefined);
    setColor(undefined);
  }, []);

  const handleNext = async () => {
    if (!validateCategoryName()) return;
    if (!isReadyCheckRequest) {
      setCheckCategoryNameValues({ title: categoryName, eventId: eventId || 1 });
      executeQuery();
      setIsReadyCheckRequest(true);
      return;
    }

    setStep(step + 1);
  };

  useEffect(() => {
    return () => {
      setSelectedParticipants([]);
      setCategoryName('');
    };
  }, []);

  const handleAdd = async () => {
    if (isAddEmployee) {
      try {
        if (freelanceEmployees) {
          const hasEmptyFields = freelanceEmployees.some(
            (employee) => !employee.firstName || !employee.lastName || !employee.phone || !employee.email
          );

          if (hasEmptyFields) {
            showToast('Поля для внештатного участника не могут быть пустыми', 'error');
            setIsSaveButtonDisabled(true);
            return;
          }

          const newFreelanceEmployees = freelanceEmployees.map((employee) => ({
            firstName: employee.firstName,
            lastName: employee.lastName,
            email: employee.email,
            phone: cleanPhoneNumber(employee.phone),
          }));

          if (editableEmployee) {
            const { data, error } = await updateUser({
              participantId: editableEmployee.id,
              input: newFreelanceEmployees[0],
            });

            if (data?.updateParticipant?.errorData?.errorText) {
              setErrorsCreateEmployee([data?.updateParticipant?.errorData]);
              return;
            }

            if (error) {
              setStatusCreateEmployee(STATUSES_CREATE_EMPLOYEE.error);
              return;
            }

            setStatusCreateEmployee(STATUSES_CREATE_EMPLOYEE.success);
            return;
          }

          const { data, error } = await createUser({ input: newFreelanceEmployees });

          if (data?.createParticipant.errorsData?.length || error) {
            setErrorsCreateEmployee(data?.createParticipant?.errorsData || []);
            return;
          }

          if (data?.createParticipant?.successIds) {
            const newIds = data?.createParticipant?.successIds.map(Number);
            setGuestSelectedParticipants((prev) => [...prev, ...newIds]);
          }

          setFreelanceStep(FreelanceEmployeeStep.STRUCTURE);
          setFreelanceEmployees([]);
        }
      } catch {
        setStatusCreateEmployee(STATUSES_CREATE_EMPLOYEE.error);
      }
    } else {
      if (!validateCategoryName() || !eventId) return;

      try {
        const { id: categoryId } = (await handleCreateCategory()) || {};

        const allParticipants = [...selectedParticipants, ...guestSelectedParticipants];

        if ((allParticipants.length || guestsData.length) && categoryId) {
          try {
            const { data } = await addParticipantsToCategory({
              categoryId,
              employeeIds: allParticipants,
              guestsData,
            });

            setTotalCount(data?.addParticipantsToCategory?.total);
          } catch (participantError) {
            setModalState('error');
            setStep(CATEGORY_MODAL_STEPS.RESULT);
            return;
          }
        }
        setGuestSelectedParticipants([]);
        setSelectedParticipants([]);
        setModalState('success');
        setStep(CATEGORY_MODAL_STEPS.RESULT);
        refetch();
      } catch (error) {
        setModalState('error');
        setStep(CATEGORY_MODAL_STEPS.RESULT);
      }
    }
  };

  const handleCreateCategory = async () => {
    if (!eventId) return;

    const res = await createCategory({ title: categoryName, eventId, description: categoryDescription, color });

    if (!res.data?.createCategory) {
      setModalState('error');
      setStep(CATEGORY_MODAL_STEPS.RESULT);
      return;
    }

    return res.data?.createCategory as Category;
  };

  const handleRefetchParticipantsCB = async (total?: number) => {
    setTotalCount(total || 0);
    setModalState('success');
    setStep(CATEGORY_MODAL_STEPS.RESULT);
    refetch();
  };

  const handleSkipParticipants = async () => {
    if (isAddEmployee) {
      setFreelanceStep(FreelanceEmployeeStep.STRUCTURE);
      setFreelanceEmployees([]);
      setEditableEmployee(undefined);
    } else {
      if (!eventId) return;
      try {
        await handleCreateCategory();
        setGuestSelectedParticipants([]);
        setSelectedParticipants([]);
        setModalState('success');
        setStep(CATEGORY_MODAL_STEPS.RESULT);
        refetch();
      } catch (error) {
        setModalState('error');
        setStep(CATEGORY_MODAL_STEPS.RESULT);
      }
    }
  };

  const handleClose = () => {
    resetModal();
    closeHandle();
  };

  const onSuccessUploadExcel = (participants: number[] | GuestsData[]) => {
    if (!participants.length) return;

    if (typeof participants[0] === 'number') {
      setSelectedParticipants(participants as number[]);
      return;
    }

    setGuestsData(
      (participants as unknown as GuestsDataRaw[]).map((participant) => ({
        email: participant.email,
        phone: participant.phone,
        firstName: participant.first_name,
        lastName: participant.last_name,
      }))
    );
  };

  const onChangeName = (value: string) => {
    if (value.length > NAME_MAX_LENGTH) return;
    setCategoryName(value);
  };

  const onChangeDescription = (value: string) => {
    if (value.length > DESCRIPTION_MAX_LENGTH) return;
    setCategoryDescription(value);
  };

  const formsForAddCategory: ReactNode[] = [
    <CategoryParticipantsList
      eventId={eventId || 1}
      participantsIds={[]}
      selectedParticipants={selectedParticipants}
      setSelectedParticipants={setSelectedParticipants}
      setParticipantsDelete={setParticipantsDelete}
      participantsDelete={participantsDelete}
    />,
    <WithExcel onSuccessUpload={onSuccessUploadExcel} setShowModal={() => {}} />,
    <FreelanceEmployee
      freelanceEmployees={freelanceEmployees}
      onChange={setFreelanceEmployees}
      onEmailError={onEmailError}
      guestSelectedParticipants={guestSelectedParticipants}
      setGuestSelectedParticipants={setGuestSelectedParticipants}
      step={freelanceStep}
      setStep={setFreelanceStep}
      invitedParticipants={[]}
      errors={errorsCreateEmployee}
      editableEmployee={editableEmployee}
      setEditableEmployee={setEditableEmployee}
    />,
  ];

  if (step === CATEGORY_MODAL_STEPS.CREATE_FORM) {
    return (
      <ModalWrapper
        active={active}
        title=""
        size="small"
        settingsContainer={{ height: 'auto', width: '480px' }}
        settingsHeader={{ padding: '0' }}
        buttons={[
          {
            children: <p {...stylex.props(styles.textBtn, styles.cancelBtn)}>Отмена</p>,
            variant: 'secondary',
            onClick: handleClose,
            settings: { width: '85px' },
          },
          {
            children: <p {...stylex.props(styles.textBtn, styles.acceptBtn)}>Далее</p>,
            variant: 'accent',
            onClick: handleNext,
            isDisabled: !!categoryNameErrors,
            settings: { width: '78px' },
          },
        ]}
      >
        <div {...stylex.props(styles.container)}>
          <div {...stylex.props(styles.headerContainer)}>
            <p {...stylex.props(styles.title)}>Добавить категорию</p>

            <UnstyledButton onClick={handleClose}>
              <XmarkIcon size="24" {...stylex.props(styles.xMarkIcon)} />
            </UnstyledButton>
          </div>

          <div {...stylex.props(styles.inputContainer)}>
            <Input
              inputMessage={categoryNameErrors || ''}
              status={categoryNameErrors ? 'error' : undefined}
              value={categoryName}
              changeFunction={onChangeName}
              labelText="Название *"
              placeholder="Введите название"
              settings={{ width: '432px', height: '36px' }}
            />

            <CategoryColorPicker value={color} setValue={setColor} />

            <div {...stylex.props(styles.textareaContainer)}>
              <p {...stylex.props(styles.textCount)}>{categoryDescription.length}/200</p>

              <TextField
                settings={{ width: '432px', height: '78px', resize: 'vertical' }}
                placeholder="Введите описание"
                labelMsg="Описание"
                value={categoryDescription}
                changeFunction={(e) => onChangeDescription(e.target.value)}
              />
            </div>
          </div>
        </div>
      </ModalWrapper>
    );
  }

  if (step === CATEGORY_MODAL_STEPS.ADD_PARTICIPANTS && !isCBGroup) {
    return (
      <ModalWrapper
        active={active}
        title={`Добавить участников в категорию: ${categoryName}`}
        size="small"
        settingsContainer={{ height: '554px', width: '920px' }}
        settingsHeader={{ width: '920px' }}
        {...stylex.props(styles.base)}
        buttons={[
          {
            children: (
              <p {...stylex.props(styles.textBtn, styles.cancelBtn)}>{isAddEmployee ? 'Отмена' : 'Пропустить'}</p>
            ),
            variant: 'secondary',
            onClick: handleSkipParticipants,
            settings: { width: '110px' },
          },
          {
            children: <p {...stylex.props(styles.textBtn, styles.acceptBtn)}>Добавить</p>,
            variant: 'accent',
            onClick: handleAdd,
            settings: { width: '97px' },
            isDisabled: isSaveButtonDisabled,
          },
        ]}
      >
        <div {...stylex.props(styles.modalContent)}>
          <EmployeeSwitcher
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
            blocks={['Орг. структура', 'Импортировать из Excel', 'Внештатный участник']}
          />
          {formsForAddCategory[activeIndex]}
        </div>
        <CreateFreelanceResultModal status={statusCreateEmployee} onClose={() => onCloseFeedbackModal()} />
      </ModalWrapper>
    );
  }

  if (step === CATEGORY_MODAL_STEPS.ADD_PARTICIPANTS && isCBGroup) {
    return (
      <AddParticipantsCBToCategoryModal
        isOpen={active}
        onClose={handleSkipParticipants}
        handleCreateCategory={handleCreateCategory}
        refetchParticipants={handleRefetchParticipantsCB}
      />
    );
  }

  if (step === CATEGORY_MODAL_STEPS.RESULT) {
    return (
      <ModalWrapper
        active={active}
        size="small"
        settingsContainer={{ height: 'auto', width: '480px' }}
        settingsHeader={{ padding: '0' }}
        buttons={[
          {
            children: <p {...stylex.props(styles.textBtn, styles.acceptBtn)}>Закрыть</p>,
            variant: 'accent',
            onClick: handleClose,
            settings: { width: '90px' },
          },
        ]}
      >
        <div {...stylex.props(styles.headerContainer)}>
          <p {...stylex.props(styles.title)}>{modalState === 'success' ? 'Категория участников создана' : 'Ошибка'}</p>
          <button onClick={handleClose} {...stylex.props(styles.xMarkIcon)}>
            <XmarkIcon size="24" />
          </button>
        </div>

        <div {...stylex.props(styles.container)}>
          <div {...stylex.props(styles.successInfo)}>
            <div {...stylex.props(styles.icon)}>{modalState === 'success' ? <SuccessIcon /> : <ErrorIcon />}</div>
            <p {...stylex.props(styles.description)}>
              {modalState === 'success' ? (
                <>
                  Категория «{categoryName}» успешно создана.{' '}
                  {totalCount ? ` В нее добавлено ${totalCount} участников` : ' Участники не были добавлены'}
                </>
              ) : (
                'Во время добавления участников, произошла непредвиденная ошибка. Повторите попытку позднее'
              )}
            </p>
          </div>
        </div>
      </ModalWrapper>
    );
  }

  return null;
};
